import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Send as SendIcon,
  Notifications as NotificationsIcon,
  People as PeopleIcon,
  LocationCity as LocationCityIcon,
  PhoneAndroid as PhoneAndroidIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useQuery, useMutation } from 'react-query';
import { pushNotificationAPI, cityAPI, userAPI } from '../services/api';
import { PushNotificationRequest, NotificationRuleInfo, City, User } from '../types';
import PushNotificationHistory from '../components/PushNotificationHistory';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notification-tabpanel-${index}`}
      aria-labelledby={`notification-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const PushNotifications: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState<PushNotificationRequest>({
    title: '',
    message: '',
    actionUrl: '',
    notificationType: 'general',
    rules: [],
    userIds: [],
    cityIds: [],
    cityNames: [],
    platform: '',
    role: '',
    activeOnly: true,
    sendImmediately: true,
    priority: 'NORMAL',
    deliveryMode: 'BOTH',
    customParameters: {},
  });

  // Queries
  const { data: rules, isLoading: rulesLoading } = useQuery(
    'notificationRules',
    () => pushNotificationAPI.getAvailableRules().then(res => res.data),
    { staleTime: 5 * 60 * 1000 } // 5 minutes
  );

  const { data: cities, isLoading: citiesLoading } = useQuery(
    'cities',
    () => cityAPI.getAll().then(res => res.data),
    { staleTime: 10 * 60 * 1000 } // 10 minutes
  );

  const { data: users, isLoading: usersLoading } = useQuery(
    'users',
    () => userAPI.getAllUsers().then(res => res.data),
    { staleTime: 5 * 60 * 1000 } // 5 minutes
  );

  // Mutations
  const sendNotificationMutation = useMutation(
    (data: PushNotificationRequest) => pushNotificationAPI.sendNotification(data),
    {
      onSuccess: (response) => {
        enqueueSnackbar(`Notification sent successfully! ${response.data.successfulDeliveries} users notified.`, {
          variant: 'success',
        });
        setActiveStep(0);
        setFormData({
          title: '',
          message: '',
          actionUrl: '',
          rules: [],
          userIds: [],
          cityIds: [],
          cityNames: [],
          platform: '',
          role: '',
          activeOnly: true,
          sendImmediately: true,
          priority: 'NORMAL',
          customParameters: {},
        });
      },
      onError: (error: any) => {
        enqueueSnackbar(`Failed to send notification: ${error.response?.data?.message || error.message}`, {
          variant: 'error',
        });
      },
    }
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleInputChange = (field: keyof PushNotificationRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRuleToggle = (ruleType: string) => {
    setFormData(prev => ({
      ...prev,
      rules: prev.rules.includes(ruleType)
        ? prev.rules.filter(r => r !== ruleType)
        : [...prev.rules, ruleType]
    }));
  };

  const handleSendNotification = () => {
    if (!formData.title || !formData.message || !formData.rules || formData.rules.length === 0) {
      enqueueSnackbar('Please fill in all required fields and select at least one rule.', {
        variant: 'warning',
      });
      return;
    }

    sendNotificationMutation.mutate(formData);
  };

  const getTargetUserCount = () => {
    let count = 0;
    
    if (formData.rules?.includes('ALL_USERS')) {
      count = users?.length || 0;
    }
    
    if (formData.rules?.includes('SELECTED_USERS')) {
      count = formData.userIds?.length || 0;
    }
    
    if (formData.rules?.includes('CITY_USERS')) {
      // This would need backend calculation for accurate count
      count = users?.filter(u => formData.cityIds?.includes(u.cityId || '')).length || 0;
    }
    
    return count;
  };

  const steps = [
    {
      label: 'Notification Content',
      description: 'Enter the notification title, message, and action URL',
    },
    {
      label: 'Targeting Rules',
      description: 'Select targeting rules and configure parameters',
    },
    {
      label: 'Preview & Send',
      description: 'Review notification and send',
    },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  if (rulesLoading || citiesLoading || usersLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <NotificationsIcon sx={{ mr: 2, color: 'primary.main' }} />
        Push Notifications
      </Typography>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="notification tabs">
          <Tab label="Send Notification" />
          <Tab label="Notification History" />
          <Tab label="Available Rules" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        {/* Quick Actions */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setActiveStep(0)}>
              <CardContent>
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">All Users</Typography>
                <Typography variant="body2" color="text.secondary">
                  Send to everyone
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setActiveStep(0)}>
              <CardContent>
                <LocationCityIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">City Users</Typography>
                <Typography variant="body2" color="text.secondary">
                  Target by city
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setActiveStep(0)}>
              <CardContent>
                <PhoneAndroidIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">Platform Users</Typography>
                <Typography variant="body2" color="text.secondary">
                  Target by platform
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setActiveStep(0)}>
              <CardContent>
                <FilterIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">Custom Rules</Typography>
                <Typography variant="body2" color="text.secondary">
                  Advanced targeting
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep} orientation="horizontal">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Step Content */}
        <Paper sx={{ p: 3 }}>
          {/* Step 1: Notification Content */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>Notification Content</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notification Title *"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter notification title"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notification Message *"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Enter notification message"
                    multiline
                    rows={3}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Action URL (Optional)"
                    value={formData.actionUrl}
                    onChange={(e) => handleInputChange('actionUrl', e.target.value)}
                    placeholder="https://example.com/action"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Notification Type</InputLabel>
                    <Select
                      value={formData.notificationType || 'general'}
                      onChange={(e) => handleInputChange('notificationType', e.target.value)}
                      label="Notification Type"
                    >
                      <MenuItem value="destination">Destination</MenuItem>
                      <MenuItem value="interchange">Interchange</MenuItem>
                      <MenuItem value="exit">Exit</MenuItem>
                      <MenuItem value="news">News</MenuItem>
                      <MenuItem value="payment">Payment</MenuItem>
                      <MenuItem value="subscription">Subscription</MenuItem>
                      <MenuItem value="maintenance">Maintenance</MenuItem>
                      <MenuItem value="delay">Delay</MenuItem>
                      <MenuItem value="general">General</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                      label="Priority"
                    >
                      <MenuItem value="LOW">Low</MenuItem>
                      <MenuItem value="NORMAL">Normal</MenuItem>
                      <MenuItem value="HIGH">High</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Delivery Mode</InputLabel>
                    <Select
                      value={formData.deliveryMode || 'BOTH'}
                      onChange={(e) => handleInputChange('deliveryMode', e.target.value)}
                      label="Delivery Mode"
                    >
                      <MenuItem value="BOTH">App + Push (Recommended)</MenuItem>
                      <MenuItem value="APP_ONLY">App Only</MenuItem>
                      <MenuItem value="PUSH_ONLY">Push Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.sendImmediately}
                        onChange={(e) => handleInputChange('sendImmediately', e.target.checked)}
                      />
                    }
                    label="Send Immediately"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 2: Targeting Rules */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>Targeting Rules</Typography>
              
              <Grid container spacing={3}>
                {/* Available Rules with quick toggles */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>Select Targeting Rules:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {rules?.map((rule) => (
                      <Chip
                        key={rule.ruleType}
                        label={rule.ruleType.replace(/_/g, ' ')}
                        onClick={() => handleRuleToggle(rule.ruleType)}
                        color={formData.rules?.includes(rule.ruleType) ? 'primary' : 'default'}
                        variant={formData.rules?.includes(rule.ruleType) ? 'filled' : 'outlined'}
                        icon={<InfoIcon />}
                      />
                    ))}
                  </Box>
                </Grid>
                {/* Quick helpers */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" onClick={() => setFormData(prev => ({ ...prev, rules: ['ALL_USERS'] }))}>All Users</Button>
                    <Button size="small" variant="outlined" onClick={() => setFormData(prev => ({ ...prev, rules: ['CITY_USERS'] }))}>City Users</Button>
                    <Button size="small" variant="outlined" onClick={() => setFormData(prev => ({ ...prev, rules: ['PLATFORM_USERS'] }))}>Platform Users</Button>
                    <Button size="small" variant="text" onClick={() => setFormData(prev => ({ ...prev, rules: [] }))}>Clear</Button>
                  </Box>
                </Grid>

                {/* Rule-specific parameters */}
                {formData.rules?.includes('SELECTED_USERS') && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Select Users</InputLabel>
                      <Select
                        multiple
                        value={formData.userIds || []}
                        onChange={(e) => handleInputChange('userIds', e.target.value)}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => {
                              const user = users?.find(u => u.id === value);
                              return (
                                <Chip key={value} label={user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username : value} />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {users?.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username} ({user.email})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {formData.rules?.includes('CITY_USERS') && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Select Cities</InputLabel>
                      <Select
                        multiple
                        value={formData.cityIds || []}
                        onChange={(e) => handleInputChange('cityIds', e.target.value)}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => {
                              const city = cities?.find(c => c.id === value);
                              return <Chip key={value} label={city?.name || value} />;
                            })}
                          </Box>
                        )}
                      >
                        {cities?.map((city) => (
                          <MenuItem key={city.id} value={city.id}>
                            {city.name} ({city.country})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {formData.rules?.includes('PLATFORM_USERS') && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Platform</InputLabel>
                      <Select
                        value={formData.platform || ''}
                        onChange={(e) => handleInputChange('platform', e.target.value)}
                        label="Platform"
                      >
                        <MenuItem value="ANDROID">Android</MenuItem>
                        <MenuItem value="IOS">iOS</MenuItem>
                        <MenuItem value="WEB">Web</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {formData.rules?.includes('ROLE_USERS') && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={formData.role || ''}
                        onChange={(e) => handleInputChange('role', e.target.value)}
                        label="Role"
                      >
                        <MenuItem value="USER">User</MenuItem>
                        <MenuItem value="ADMIN">Admin</MenuItem>
                        <MenuItem value="MODERATOR">Moderator</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.activeOnly}
                        onChange={(e) => handleInputChange('activeOnly', e.target.checked)}
                      />
                    }
                    label="Active Users Only"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 3: Preview & Send */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>Preview & Send</Typography>
              
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {formData.title}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formData.message}
                  </Typography>
                  {formData.actionUrl && (
                    <Typography variant="body2" color="text.secondary">
                      Action URL: {formData.actionUrl}
                    </Typography>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2">
                    <strong>Targeting:</strong> {formData.rules?.map(r => r.replace(/_/g, ' ')).join(', ') || 'No rules selected'}
                  </Typography>
                  <Typography variant="subtitle2">
                    <strong>Estimated Users:</strong> {getTargetUserCount()}
                  </Typography>
                  <Typography variant="subtitle2">
                    <strong>Priority:</strong> {formData.priority}
                  </Typography>
                </CardContent>
              </Card>

              <Alert severity="info" sx={{ mb: 2 }}>
                This notification will be sent to approximately {getTargetUserCount()} users.
                Please review the content and targeting rules before sending.
              </Alert>
            </Box>
          )}

          {/* Step Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSendNotification}
                  disabled={sendNotificationMutation.isLoading}
                  startIcon={sendNotificationMutation.isLoading ? <CircularProgress size={20} /> : <SendIcon />}
                >
                  {sendNotificationMutation.isLoading ? 'Sending...' : 'Send Notification'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={
                    activeStep === 0 
                      ? (!formData.title || !formData.message) // Step 1: Only need title and message
                      : (!formData.rules || formData.rules.length === 0) // Step 2: Need rules selected
                  }
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </TabPanel>

      {/* Tab 2: Notification History */}
      <TabPanel value={tabValue} index={1}>
        <PushNotificationHistory
          notifications={[]} // TODO: Add notification history API
          onRefresh={() => {}} // TODO: Implement refresh
        />
      </TabPanel>

      {/* Tab 3: Available Rules */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Available Targeting Rules</Typography>
          <Grid container spacing={2}>
            {rules?.map((rule) => (
              <Grid item xs={12} md={6} key={rule.ruleType}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {rule.ruleType.replace(/_/g, ' ')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {rule.description}
                    </Typography>
                    {rule.requiredParameters && rule.requiredParameters.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Required: {rule.requiredParameters.join(', ')}
                        </Typography>
                      </Box>
                    )}
                    {rule.optionalParameters && rule.optionalParameters.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Optional: {rule.optionalParameters.join(', ')}
                        </Typography>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default PushNotifications;
